import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiPhoneCall } from "react-icons/fi";
import {
  extractPhoneOptions,
  requestZadarmaCall,
  subscribeZadarmaTelephony,
} from "./zadarmaTelephony";

function getDisabledTitle({ phoneCount, ready, active }) {
  if (!phoneCount) return "Sin telefono";
  if (!ready) return "Telefonia no disponible";
  if (active) return "Ya hay una llamada activa";
  return "Llamar";
}

export default function ZadarmaCallButton({
  phones,
  label,
  entityType,
  entityId,
  className = "",
  disabled = false,
  disabledTitle = "",
  showWhenUnavailable = false,
}) {
  const buttonRef = useRef(null);
  const options = useMemo(() => extractPhoneOptions(phones), [phones]);
  const [telephonyState, setTelephonyState] = useState(() => ({
    enabled: false,
    ready: false,
    active: false,
  }));
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => subscribeZadarmaTelephony(setTelephonyState), []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({
        top: Math.round(rect.bottom + 7),
        right: Math.max(8, Math.round(window.innerWidth - rect.right)),
      });
    };

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (event.target?.closest?.(".crm-callMenu")) return;
      setMenuOpen(false);
    };

    updateMenuPosition();
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [menuOpen]);

  const isTelephonyUnavailable = !telephonyState.enabled;
  const isDisabled = disabled || isTelephonyUnavailable || !options.length || !telephonyState.ready || telephonyState.active;
  let title = options.length > 1 ? "Elegir numero" : `Llamar a ${options[0]?.label || ""}`;
  if (isDisabled) {
    title =
      disabled && disabledTitle
        ? disabledTitle
        : getDisabledTitle({
            phoneCount: options.length,
            ready: telephonyState.ready,
            active: telephonyState.active,
          });
  }

  const startCall = (option) => {
    if (!option || isDisabled) return;
    setMenuOpen(false);
    requestZadarmaCall({
      number: option.number,
      rawNumber: option.label,
      label,
      entityType,
      entityId,
    });
  };

  const handleClick = () => {
    if (isDisabled) return;
    if (options.length === 1) {
      startCall(options[0]);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPosition({
        top: Math.round(rect.bottom + 7),
        right: Math.max(8, Math.round(window.innerWidth - rect.right)),
      });
    }
    setMenuOpen((current) => !current);
  };

  if (!telephonyState.enabled && !showWhenUnavailable) {
    return null;
  }

  return (
    <span className={`crm-callButtonWrap ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        className={`crm-iconButton crm-callButton${menuOpen ? " crm-callButton--open" : ""}`}
        onClick={handleClick}
        disabled={isDisabled}
        title={title}
        aria-label={title}
        aria-haspopup={options.length > 1 ? "menu" : undefined}
        aria-expanded={options.length > 1 ? menuOpen : undefined}
      >
        <FiPhoneCall aria-hidden="true" />
        {options.length > 1 ? <FiChevronDown className="crm-callButtonChevron" aria-hidden="true" /> : null}
      </button>

      {menuOpen && menuPosition
        ? createPortal(
            <div
              className="crm-callMenu"
              role="menu"
              style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
            >
              {options.map((option) => (
                <button
                  key={option.number}
                  type="button"
                  className="crm-callMenuItem"
                  onClick={() => startCall(option)}
                  role="menuitem"
                >
                  <FiPhoneCall aria-hidden="true" />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}
